import React, { useState } from "react";
import Header from "../Header";
import Footer from "../Footer";
import SEO from "../SEO";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Card, CardContent } from "../ui/card";
import { 
  PhoneIcon,
  MailIcon,
  MapPinIcon,
  ClockIcon,
  MessageCircleIcon,
  HeadphonesIcon,
  SendIcon
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import Breadcrumb from "../Breadcrumb";
import { contactService } from "../../services";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    city: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      const inquiryData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject || "General Inquiry",
        message: formData.message,
        type: "general",
        city: formData.city
      };

      await contactService.submitInquiry(inquiryData);
      toast.success("Your message has been sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "", city: "" });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error("Failed to send message. Please try again.");
    }
  };

  const contactMethods = [
    {
      icon: PhoneIcon,
      title: "Call Us",
      description: "Speak to our medical coordinators",
      contact: "+91 93117 20045",
      subtext: "Available 24/7",
      color: "bg-blue-500"
    },
    {
      icon: MailIcon,
      title: "Email Us",
      description: "Send us your queries",
      contact: "tlc@thelivcure.com",
      subtext: "Response within 24 hours",
      color: "bg-green-500"
    },
    {
      icon: MessageCircleIcon,
      title: "LivChat",
      description: "Instant support online",
      contact: "Chat Now",
      subtext: "Available 24/7",
      color: "bg-purple-500"
    },
    {
      icon: HeadphonesIcon,
      title: "WhatsApp",
      description: "Message us on WhatsApp",
      contact: "+91 93117 20045",
      subtext: "Quick responses",
      color: "bg-green-600"
    }
  ];

  const offices = [
    {
      city: "Delhi (Head Office)",
      address: "G-627, Sangam Vihar, New Delhi - 110062",
      phone: "+91 93117 20046",
      email: "tlc@thelivcure.com"
    },
  ];

  const faqs = [
    {
      question: "How do I book an appointment?",
      answer: "You can book an appointment through our website, mobile app, or by calling our helpline number +91 93117 20045."
    },
    {
      question: "Do you accept insurance?",
      answer: "Yes, we work with all major insurance providers and offer cashless treatment options."
    },
    {
      question: "What is the consultation fee?",
      answer: "Consultation fees vary by doctor and specialty, typically ranging from ₹500 to ₹1000. First consultation is often free."
    },
    {
      question: "Are your doctors certified?",
      answer: "All our doctors are highly qualified, board-certified specialists with extensive experience in their respective fields."
    }
  ];

  const contactStructuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "MedicalOrganization",
      "name": "TheLivCure",
      "telephone": ["+919311720045", "+919311720046"],
      "email": "tlc@thelivcure.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "G-627, Sangam Vihar",
        "addressLocality": "New Delhi",
        "postalCode": "110062",
        "addressCountry": "IN"
      },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+919311720045",
          "contactType": "customer service",
          "availableLanguage": ["English", "Hindi"]
        },
        {
          "@type": "ContactPoint",
          "telephone": "+919311720046",
          "contactType": "emergency",
          "availableLanguage": ["English", "Hindi"]
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Contact Us"
        description="Contact TheLivCure for medical consultations and appointments. Call +91 9311720045 or email tlc@thelivcure.com. Located in Delhi, serving patients across NCR region."
        keywords="contact thelivcure, medical consultation, appointment booking, Delhi medical center, healthcare contact, medical helpline"
        url="/contact"
        structuredData={contactStructuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb 
            items={[
              { label: "Contact Us" }
            ]} 
          />
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Get in <span className="text-blue-600">Touch</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8 px-4">
              We're here to help you with all your healthcare needs. Reach out to us anytime, anywhere.
            </p>
          </div>

          {/* Contact Methods */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto">
            {contactMethods.map((method, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-4 sm:p-6">
                  <div className={`w-16 h-16 ${method.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <method.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{method.title}</h3>
                  <p className="text-sm text-gray-600 mb-3">{method.description}</p>
                  <div className="text-blue-600 font-medium mb-1">{method.contact}</div>
                  <div className="text-xs text-gray-500">{method.subtext}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
            {/* Contact Form */}
            <Card>
              <CardContent className="p-4 sm:p-6 lg:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="Your phone number"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="Your email"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject
                      </label>
                      <Select value={formData.subject} onValueChange={(value) => setFormData({...formData, subject: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select subject" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="appointment">Book Appointment</SelectItem>
                          <SelectItem value="consultation">Medical Consultation</SelectItem>
                          <SelectItem value="insurance">Insurance Query</SelectItem>
                          <SelectItem value="feedback">Feedback</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City
                      </label>
                      <Select value={formData.city} onValueChange={(value) => setFormData({...formData, city: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select city" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="delhi">Delhi</SelectItem>
                          <SelectItem value="gurgaon">Gurgaon</SelectItem>
                          <SelectItem value="noida">Noida</SelectItem>
                          <SelectItem value="faridabad">Faridabad</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="How can we help you?"
                      rows={4}
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                    <SendIcon className="w-4 h-4 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Office Locations */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Offices</h2>
              <div className="space-y-4">
                {offices.map((office, index) => (
                  <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">{office.city}</h3>
                      <div className="space-y-2">
                        <div className="flex items-start space-x-3">
                          <MapPinIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-600">{office.address}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <PhoneIcon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{office.phone}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <MailIcon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{office.email}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Quick answers to common questions</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Business Hours */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-blue-600 text-white rounded-3xl p-8">
            <ClockIcon className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">24/7 Available</h2>
            <p className="text-lg mb-6 opacity-90">
              Our medical coordinators and emergency services are available round the clock
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="font-semibold mb-1">Phone Support</div>
                <div className="opacity-90">24 hours, 7 days a week</div>
              </div>
              <div>
                <div className="font-semibold mb-1">Emergency Care</div>
                <div className="opacity-90">Always available</div>
              </div>
              <div>
                <div className="font-semibold mb-1">Online Support</div>
                <div className="opacity-90">24/7 chat & email</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs;